import { Badge, Button, Empty, Tooltip, Typography, theme } from "antd";
import {
  AuditOutlined,
  FileAddOutlined,
  FileTextOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { ProcessSection, ProcessWorkflow } from "../../../api";
import type { DragPayload } from "./CanvasContext";

const { Text } = Typography;

type Props = {
  sections: ProcessSection[];
  workflows: ProcessWorkflow[];
  onSelect: (selection: { type: "section" | "workflow"; id: number }) => void;
  setDragging: (payload: DragPayload) => void;
  onCreateSection: () => void;
  onAddWorkflow: () => void;
};

const TrayItem = ({
  label,
  icon,
  onSelect,
  onDragStart,
  onDragEnd,
}: {
  label: string;
  icon: React.ReactNode;
  onSelect: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
}) => {
  const { token } = theme.useToken();
  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData("text/plain", label);
        e.dataTransfer.effectAllowed = "move";
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      onClick={onSelect}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 8px",
        marginBottom: 6,
        borderRadius: token.borderRadius,
        cursor: "grab",
        background: token.colorFillQuaternary,
        border: `1px dashed ${token.colorBorder}`,
        fontSize: 12,
      }}
    >
      <span style={{ color: token.colorTextTertiary, display: "flex" }}>
        {icon}
      </span>
      <span
        style={{
          flex: 1,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </span>
    </div>
  );
};

/**
 * Sections and sign-offs that are not in any step yet.
 *
 * This is where a section lives between being created and being placed, and it
 * is deliberately visible rather than hidden behind a menu: a section that
 * belongs to no step is never filled in by anybody, which is a silent way for a
 * process to be wrong. Leaving them on screen, next to the canvas they are
 * dragged onto, makes "nothing is stranded" something you can see.
 */
const ItemTray = (props: Props) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const empty = props.sections.length === 0 && props.workflows.length === 0;

  return (
    <div
      style={{
        padding: 10,
        height: "100%",
        overflowY: "auto",
        borderRight: `1px solid ${token.colorBorderSecondary}`,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        <Text type="secondary" style={{ fontSize: 11, letterSpacing: 0.4 }}>
          {t("NOT IN ANY STEP")}
        </Text>
        <Badge
          count={props.sections.length + props.workflows.length}
          size="small"
          color={empty ? token.colorTextQuaternary : token.colorWarning}
          showZero
        />
      </div>

      {empty ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <Text type="secondary" style={{ fontSize: 11 }}>
              {t("Everything is placed.")}
            </Text>
          }
          style={{ margin: "16px 0" }}
        />
      ) : (
        <>
          {props.sections.map((section) => (
            <TrayItem
              key={`s${section.id}`}
              label={t(section.title)}
              icon={<FileTextOutlined />}
              onSelect={() => props.onSelect({ type: "section", id: section.id })}
              onDragStart={() =>
                props.setDragging({ kind: "form", id: section.id })
              }
              onDragEnd={() => props.setDragging(null)}
            />
          ))}
          {props.workflows.map((workflow) => (
            <TrayItem
              key={`w${workflow.id}`}
              label={workflow.name?.trim() || t("Untitled sign-off")}
              icon={<AuditOutlined />}
              onSelect={() =>
                props.onSelect({ type: "workflow", id: workflow.id as number })
              }
              onDragStart={() =>
                props.setDragging({ kind: "workflow", id: workflow.id as number })
              }
              onDragEnd={() => props.setDragging(null)}
            />
          ))}
        </>
      )}

      <div style={{ marginTop: 12, display: "grid", gap: 6 }}>
        <Tooltip title={t("Create a new section and open the form designer")}>
          <Button
            size="small"
            block
            icon={<FileAddOutlined />}
            onClick={props.onCreateSection}
          >
            {t("New section")}
          </Button>
        </Tooltip>
        <Button
          size="small"
          block
          icon={<PlusOutlined />}
          onClick={props.onAddWorkflow}
        >
          {t("New sign-off")}
        </Button>
      </div>
    </div>
  );
};

export default ItemTray;
